import type { ReactPermissionRoute } from "@/stores/react/authStore";
import type { AdminRouteObject } from "./index";

const publicMenuPaths = new Set(["/", "/login", "/403", "/404", "/500", "*"]);

// 旧 Vue pageRoutes 的一级菜单顺序与范围，待办、错误页等入口不参与顶部业务菜单。
const topLevelMenuOrder = [
    "/dashboard",
    "/productManagement",
    "/productActivity",
    "/tradeManage",
    "/lianYunUser",
    "/userOperation",
    "/supplierManagement",
    "/systemMangement"
];

type LegacyMenuBlueprint = {
    path: string;
    title?: string;
    children?: LegacyMenuBlueprint[];
};

/**
 * 旧 Vue 实际展示的菜单树。
 *
 * React 路由表还包含详情、表单、兼容别名和转换期间新增的直达页面；这些路由需要继续可访问，
 * 但不能自动成为导航项。这里仅声明受转换影响模块的可见节点、顺序与分组，避免再把路由表
 * 的物理结构误当成产品菜单结构。
 */
const legacyVisibleMenuBlueprints: Record<string, LegacyMenuBlueprint[]> = {
    "/dashboard": [
        { path: "/dashboard/index" },
        { path: "/dashboard/goodsOperationAnalysis" },
        { path: "/dashboard/functionalOperationAnalysis" }
    ],
    "/tradeManage": [
        { path: "/tradeManage/tradeOrder" },
        { path: "/tradeManage/collectionManage" },
        { path: "/tradeManage/refundManage" },
        { path: "/tradeManage/invoiceManage" },
        { path: "/tradeManage/zoneContract" },
        { path: "/tradeManage/zoneReservationOrder" },
        { path: "/tradeManage/zoneAccountManagement" }
    ],
    "/lianYunUser": [
        { path: "/lianYunUser/infoManage" },
        { path: "/lianYunUser/institutionApply" },
        { path: "/lianYunUser/memberLevel" },
        { path: "/lianYunUser/activationCode" },
        { path: "/lianYunUser/recharge" }
    ],
    "/userOperation": [
        { path: "/userOperation/sampleApply" },
        { path: "/userOperation/fundingClues" },
        { path: "/userOperation/messageFeedback" },
        { path: "/userOperation/notificationManage" },
        {
            path: "/userOperation/adBanner",
            title: "广告管理",
            children: [
                { path: "/userOperation/adBanner/banner", title: "移动端" },
                { path: "/userOperation/adBanner/ad", title: "PC端" },
                { path: "/userOperation/adPageManage" },
                { path: "/userOperation/popUpAdvert" }
            ]
        },
        {
            path: "/userOperation/lianYunAbout",
            title: "关于链云砂石",
            children: [
                { path: "/userOperation/lianYunAbout/aboutUs" },
                { path: "/userOperation/lianYunAbout/userAgreement" },
                { path: "/userOperation/lianYunAbout/privacyAgreement" },
                { path: "/userOperation/lianYunAbout/memberAgreement" },
                { path: "/userOperation/lianYunAbout/helpCenter" },
                { path: "/userOperation/lianYunAbout/aiPrivacyAgreement" },
                { path: "/userOperation/lianYunAbout/aiUserAgreement" }
            ]
        },
        { path: "/userOperation/searchFound" },
        { path: "/userOperation/appVersion" }
    ],
    "/systemMangement": [
        {
            path: "/systemMangement/systemManage",
            title: "组织用户",
            children: [
                { path: "/systemMangement/systemManage/organizationalStructure" },
                { path: "/systemMangement/systemManage/backendAccount" },
                { path: "/systemMangement/systemManage/role" }
            ]
        }
    ]
};

/**
 * 旧 Vue 路由中 `meta.hidden: true` 的页面入口。
 * React 转换阶段曾整体遗漏该字段；在菜单转换边界集中补回，路由仍可直达，但不会出现在导航中。
 */
const legacyHiddenMenuPaths = new Set([
    // 管理中心的兼容详情入口不参与可见菜单，但需要挂回旧系统管理菜单以保持布局高亮。
    "/manageCenter/systemManage/role/detail",
    "/manageCenter/systemManage/role/detail/:id",
    "/dashboard/consignmentGoodsAnalysisDetail",
    "/intelligentAssistant/aiHistoricalDialogue/:id",
    "/intelligentAssistant/aiCorpus/publish/add",
    "/intelligentAssistant/aiCorpus/publish/edit/:id",
    "/intelligentAssistant/aiCorpus/publish/info/:id",
    "/lianYunUser/userInfo",
    "/lianYunUser/communicateRecordDetail",
    "/lianYunUser/AddCommunicateRecord",
    "/lianYunUser/editCommunicateRecord",
    "/lianYunUser/institutionApply/info/:id",
    "/lianYunUser/institutionApply/audit/:id",
    "/lianYunUser/institutionApply/form/:id",
    "/lianYunUser/memberLevelinfo",
    "/lianYunUser/activationCodeAdd",
    "/logistics/findLocation/:mmsi",
    "/logistics/findTrack/:mmsi/:dateRange",
    "/logistics/portWharfBerth/portAdd",
    "/logistics/portWharfBerth/portEdit",
    "/logistics/portWharfBerth/portDetail",
    "/logistics/portWharfBerth/wharfAdd",
    "/logistics/portWharfBerth/wharfEdit",
    "/logistics/portWharfBerth/wharfDetail",
    "/logistics/portWharfBerth/berthAdd",
    "/logistics/portWharfBerth/berthEdit",
    "/logistics/portWharfBerth/berthInfo",
    "/logistics/device/deviceManagement/add",
    "/logistics/device/deviceManagement/edit/:id",
    "/logistics/device/deviceManagement/info/:id",
    "/logistics/device/deviceClaim/form/:id",
    "/logistics/device/deviceClaim/info/:id",
    "/logistics/device/installPromotionRecord",
    "/logistics/device/installPromotionRecord/info/:id",
    "/logistics/device/installPromotionRecord/form",
    "/logistics/cluesManage/shippingOwnerCluesDetail/:id",
    "/logistics/cluesManage/platformCluesManage/detail/:id",
    "/logistics/cluesManage/electronicFenceRecord/:id",
    "/logistics/emptyShipInformationDetail/:id",
    "/logistics/emptyShipInformationAdd",
    "/logistics/emptyShipInformationEdit/:id",
    "/logistics/shipManage/shipInfo/shipInfoEdit/:id",
    "/logistics/shipManage/shipInfo/shipInfoDetail/:id",
    "/logistics/shipManage/shipInfo/waterGaugeBulkUploads/:shipId",
    "/logistics/shipManage/shipInfo/contactInformationUpload",
    "/logistics/shipManage/shipInfo/shipInfoBind/:id/:band",
    "/logistics/shipManage/shipCertification/info/:id",
    "/logistics/shipManage/shipCertification/apply/:id",
    "/logistics/shippingNeed/platformShipNeedEdit/:id",
    "/logistics/shippingNeed/platformShipNeedInfo/:id",
    "/logistics/shippingNeed/platformShipInviteShipMap/:id",
    // Vue 的可选参数路由在 React 中拆成了无参数与带参数两个入口，两者均只用于表单跳转。
    "/logistics/shippingNeed/platformShipNeedAdd",
    "/logistics/shippingNeed/platformShipNeedAdd/:id",
    "/logistics/shippingNeed/waybillManagementAdd",
    "/logistics/shippingNeed/waybillManagementEdit/:id",
    "/logistics/shippingNeed/waybillManagementInfo/:id",
    "/logistics/oil/oilSiteAdd",
    "/logistics/oil/oilSiteEdit/:id",
    "/logistics/oil/OilSiteDetail/:id",
    "/logistics/oil/oilTypeManagement/detail/:id",
    "/logistics/oil/oilTypeManagement/add",
    "/logistics/oil/oilTypeManagement/check/:id",
    "/logistics/monitor/monitorWarn/:id",
    "/logistics/featuresFeedback/info/:id",
    "/logistics/transportCapacityManagement/detail/:id",
    "/logistics/transportCapacityManagement/capacityManagement/addCapacity",
    "/productActivity/activity/detail",
    "/activityManagement/activeGoodsDetail",
    "/productActivity/activity/add",
    "/productActivity/activity/edit",
    "/productManagement/consignment/add",
    "/productManagement/consignment/edit",
    "/productManagement/consignment/detail",
    "/productManagement/consignment/audit",
    "/productManagement/groupPurchase/detail",
    "/productManagement/groupPurchase/add",
    "/productManagement/groupPurchase/edit",
    "/productManagement/groupPurchase/audit",
    "/productManagement/bargain/detail",
    "/productManagement/bargain/add",
    "/productManagement/bargain/edit",
    "/productManagement/bargain/audit",
    "/supplierManagement/enterpriseManage/detail",
    "/supplierManagement/enterpriseManage/add",
    "/supplierManagement/enterpriseManage/edit",
    "/systemMangement/systemManage/organizationalStructure/add",
    "/systemMangement/systemManage/organizationalStructure/edit",
    "/systemMangement/systemManage/backendAccount/add",
    "/systemMangement/systemManage/backendAccount/edit",
    "/systemMangement/systemManage/role/add",
    "/systemMangement/systemManage/role/detail",
    "/systemMangement/systemManage/role/edit",
    "/tradeManage/tradeOrder/add",
    "/tradeManage/tradeOrder/edit",
    "/tradeManage/tradeOrder/detail",
    "/tradeManage/tradeOrder/audit",
    "/tradeManage/delivery/detail",
    "/tradeManage/collection/detail",
    "/tradeManage/refund/add",
    "/tradeManage/refund/edit",
    "/tradeManage/refund/detail",
    "/tradeManage/refund/audit",
    "/tradeManage/invoiceDetail",
    "/tradeManage/invoiceAdd",
    "/tradeManage/zoneContract/add",
    "/tradeManage/zoneContract/edit/:id",
    "/tradeManage/zoneContractDetail",
    "/tradeManage/zoneReservationOrderDetail",
    "/tradeManage/rechargeDetails/:id",
    "/userOperation/sampleApply/detail",
    "/userOperation/fundingClues/detail",
    "/userOperation/messageFeedback/details",
    "/userOperation/notificationAdd",
    "/userOperation/NotificationEdit",
    "/userOperation/notificationInfo",
    "/userOperation/adBanner/adEdit",
    "/userOperation/adBanner/twoEditType",
    // React 转换时移除了旧 Vue 的 adBanner 路径分组，按当前注册路径继续保留隐藏语义。
    "/userOperation/adPageManage/detail",
    "/userOperation/addAd",
    "/userOperation/adPageManage/edit",
    "/userOperation/popUpAdvert/add",
    "/userOperation/popUpAdvert/edit",
    "/userOperation/popUpAdvert/detail",
    "/userOperation/lianYunAbout/helpCenter/info",
    "/userOperation/lianYunAbout/helpCenter/add",
    "/userOperation/lianYunAbout/helpCenter/edit"
]);

/**
 * 旧 Vue `meta.activeMenu` 中无法靠 URL 父子前缀推导的菜单归属关系。
 * React 路由可直接在 handle 中声明 activeMenu；此表为转换期存量路由恢复同一全局契约。
 */
const legacyActiveMenuPaths: Record<string, string> = {
    // 管理中心中的兼容角色详情入口沿用旧系统管理菜单，确保详情页的全局布局与旧版一致。
    "/manageCenter/systemManage/role/detail": "/systemMangement/systemManage/role",
    "/manageCenter/systemManage/role/detail/:id": "/systemMangement/systemManage/role",
    "/dashboard/consignmentGoodsAnalysisDetail": "/dashboard/goodsOperationAnalysis",
    "/lianYunUser/userInfo": "/lianYunUser/infoManage",
    "/lianYunUser/communicateRecordDetail": "/lianYunUser/infoManage",
    "/lianYunUser/AddCommunicateRecord": "/lianYunUser/infoManage",
    "/lianYunUser/editCommunicateRecord": "/lianYunUser/infoManage",
    "/lianYunUser/memberLevelinfo": "/lianYunUser/memberLevel",
    "/lianYunUser/activationCodeAdd": "/lianYunUser/activationCode",
    "/logistics/findTrack/:mmsi/:dateRange": "/logistics/findLocation",
    "/logistics/portWharfBerth/portAdd": "/logistics/portWharfBerth/portManagement",
    "/logistics/portWharfBerth/portEdit": "/logistics/portWharfBerth/portManagement",
    "/logistics/portWharfBerth/portDetail": "/logistics/portWharfBerth/portManagement",
    "/logistics/portWharfBerth/wharfAdd": "/logistics/portWharfBerth/wharfManagement",
    "/logistics/portWharfBerth/wharfEdit": "/logistics/portWharfBerth/wharfManagement",
    "/logistics/portWharfBerth/wharfDetail": "/logistics/portWharfBerth/wharfManagement",
    "/logistics/portWharfBerth/berthAdd": "/logistics/portWharfBerth/berthManagement",
    "/logistics/portWharfBerth/berthEdit": "/logistics/portWharfBerth/berthManagement",
    "/logistics/portWharfBerth/berthInfo": "/logistics/portWharfBerth/berthManagement",
    "/logistics/cluesManage/shippingOwnerCluesDetail/:id": "/logistics/cluesManage/shippingOwnerClues",
    "/logistics/emptyShipInformationDetail/:id": "/logistics/emptyShipInformation",
    "/logistics/shippingNeed/platformShipNeedAdd": "/logistics/shippingNeed/platformShipNeed",
    "/logistics/shippingNeed/platformShipNeedAdd/:id": "/logistics/shippingNeed/platformShipNeed",
    "/logistics/shippingNeed/platformShipNeedEdit/:id": "/logistics/shippingNeed/platformShipNeed",
    "/logistics/shippingNeed/platformShipNeedInfo/:id": "/logistics/shippingNeed/platformShipNeed",
    "/logistics/shippingNeed/platformShipInviteShipMap/:id": "/logistics/shippingNeed/platformShipNeed",
    "/logistics/shippingNeed/waybillManagementAdd": "/logistics/shippingNeed/waybillManagement",
    "/logistics/shippingNeed/waybillManagementEdit/:id": "/logistics/shippingNeed/waybillManagement",
    "/logistics/shippingNeed/waybillManagementInfo/:id": "/logistics/shippingNeed/waybillManagement",
    "/logistics/oil/oilSiteAdd": "/logistics/oil/oilSite",
    "/logistics/oil/oilSiteEdit/:id": "/logistics/oil/oilSite",
    "/logistics/oil/OilSiteDetail/:id": "/logistics/oil/oilSite",
    "/logistics/transportCapacityManagement/detail/:id": "/logistics/transportCapacityManagement/capacityManagement",
    "/tradeManage/delivery/detail": "/tradeManage/tradeOrder",
    "/tradeManage/collection/detail": "/tradeManage/collectionManage",
    "/tradeManage/refund/add": "/tradeManage/refundManage",
    "/tradeManage/refund/edit": "/tradeManage/refundManage",
    "/tradeManage/refund/detail": "/tradeManage/refundManage",
    "/tradeManage/refund/audit": "/tradeManage/refundManage",
    "/tradeManage/invoiceDetail": "/tradeManage/invoiceManage",
    "/tradeManage/invoiceAdd": "/tradeManage/invoiceManage",
    "/tradeManage/zoneContractDetail": "/tradeManage/zoneContract",
    "/tradeManage/zoneReservationOrderDetail": "/tradeManage/zoneReservationOrder",
    "/tradeManage/rechargeDetails/:id": "/tradeManage/zoneAccountManagement",
    "/userOperation/notificationAdd": "/userOperation/notificationManage",
    "/userOperation/NotificationEdit": "/userOperation/notificationManage",
    "/userOperation/notificationInfo": "/userOperation/notificationManage",
    "/userOperation/adBanner/adEdit": "/userOperation/adBanner/ad",
    "/userOperation/adBanner/twoEditType": "/userOperation/adBanner/ad",
    "/userOperation/adBanner/addAd": "/userOperation/adPageManage",
    "/userOperation/addAd": "/userOperation/adPageManage"
};

const joinPath = (parentPath: string, childPath: string) => {
    if (!childPath) {
        return parentPath || "/";
    }
    if (childPath.startsWith("/")) {
        return childPath;
    }
    return `${parentPath.replace(/\/$/, "")}/${childPath}`.replace(/\/+/g, "/");
};

/** 判断 activeMenu 是否归属于指定一级菜单。 */
const isPathInsideMenu = (path: string, menuPath: string) =>
    path === menuPath || (menuPath !== "/" && path.startsWith(`${menuPath}/`));

const hasPermission = (route: AdminRouteObject, permissions: string[]) => {
    const required = route.handle?.permissions || [];
    return required.length === 0 || required.some((permission: string) => permissions.includes(permission));
};

const filterAuthorizedRoutes = (routes: AdminRouteObject[], permissions: string[]): AdminRouteObject[] =>
    routes.reduce<AdminRouteObject[]>((result, route) => {
        const children = route.children ? filterAuthorizedRoutes(route.children, permissions) : [];
        const visible = route.children ? children.length > 0 : hasPermission(route, permissions);

        if (visible) {
            result.push(route.children ? { ...route, children } : route);
        }
        return result;
    }, []);

const toMenuRoute = (route: AdminRouteObject, parentPath = ""): ReactPermissionRoute => {
    const routePath = route.path || (route.index ? "index" : "");
    const fullPath = joinPath(parentPath, routePath);
    const children = route.children
        ?.map(child => toMenuRoute(child, fullPath))
        .filter((child, index, routes) => routes.findIndex(item => item.path === child.path) === index);
    const meta: NonNullable<ReactPermissionRoute["meta"]> = route.handle ? { ...route.handle } : {};

    if (legacyHiddenMenuPaths.has(fullPath)) {
        meta.hidden = true;
    }

    // 新路由声明优先；未迁移的存量路由继续沿用 Vue 的全局菜单归属关系。
    if (typeof meta.activeMenu !== "string" && legacyActiveMenuPaths[fullPath]) {
        meta.activeMenu = legacyActiveMenuPaths[fullPath];
    }

    return {
        name: route.id,
        path: fullPath,
        children: children?.length ? children : undefined,
        meta
    };
};

/** 递归收集菜单节点，供旧版菜单蓝图从扁平或嵌套路由中按完整路径取回页面。 */
const flattenMenuRoutes = (routes: ReactPermissionRoute[]): ReactPermissionRoute[] =>
    routes.flatMap(route => [route, ...flattenMenuRoutes(route.children || [])]);

/**
 * 用旧 Vue 的可见菜单蓝图重新组织导航，同时把未入选的路由保留为隐藏节点。
 * 隐藏节点仍参与标签标题等路径查找，但不会污染侧边导航。
 */
const applyLegacyMenuBlueprint = (
    route: ReactPermissionRoute,
    blueprint: LegacyMenuBlueprint[]
): ReactPermissionRoute => {
    const sourceRoutes = flattenMenuRoutes(route.children || []);
    const sourceByPath = new Map(sourceRoutes.map(sourceRoute => [sourceRoute.path, sourceRoute]));
    const selectedPaths = new Set<string>();

    const buildVisibleRoutes = (definitions: LegacyMenuBlueprint[]): ReactPermissionRoute[] =>
        definitions.flatMap(definition => {
            const sourceRoute = sourceByPath.get(definition.path);
            const children = definition.children ? buildVisibleRoutes(definition.children) : [];

            // 当前账号没有任一子项权限时，不生成空的 Vue 分组菜单。
            if (!sourceRoute && children.length === 0) {
                return [];
            }
            if (sourceRoute?.path) {
                selectedPaths.add(sourceRoute.path);
            }

            return [
                {
                    ...sourceRoute,
                    path: definition.path,
                    meta: {
                        ...sourceRoute?.meta,
                        title: definition.title || sourceRoute?.meta?.title
                    },
                    children: children.length > 0 ? children : undefined
                }
            ];
        });

    const visibleRoutes = buildVisibleRoutes(blueprint);
    const hiddenRoutes = sourceRoutes
        .filter(sourceRoute => !sourceRoute.children?.length && !selectedPaths.has(sourceRoute.path || ""))
        .map(sourceRoute => ({
            ...sourceRoute,
            meta: { ...sourceRoute.meta, hidden: true }
        }));

    return {
        ...route,
        children: [...visibleRoutes, ...hiddenRoutes]
    };
};

/**
 * 将 React 路由表转换成旧 Vue 布局使用的菜单树。
 * 菜单路径统一为绝对路径，避免子路由直接 navigate("index") 导致上下文错误。
 */
export function buildMenuRoutes(routes: AdminRouteObject[], permissions: string[]): ReactPermissionRoute[] {
    const businessRoutes = topLevelMenuOrder
        .map(path => routes.find(route => route.path === path))
        .filter((route): route is AdminRouteObject => {
            const path = route?.path || "";
            return Boolean(route) && Boolean(path) && !publicMenuPaths.has(path);
        });

    const menuRoutes = filterAuthorizedRoutes(businessRoutes, permissions).map(route => {
        const menuRoute = toMenuRoute(route);
        const blueprint = legacyVisibleMenuBlueprints[menuRoute.path || ""];
        return blueprint ? applyLegacyMenuBlueprint(menuRoute, blueprint) : menuRoute;
    });

    // 绝对历史路由可能因 React Router 限制注册在业务模块之外，仍需按 activeMenu 归回所属菜单树。
    const allAuthorizedMenuRoutes = filterAuthorizedRoutes(routes, permissions).map(route => toMenuRoute(route));
    const detachedRoutes = flattenMenuRoutes(allAuthorizedMenuRoutes).filter(route => {
        const activeMenu = route.meta?.activeMenu;
        return route.meta?.hidden === true && typeof activeMenu === "string" && activeMenu.length > 0;
    });

    return menuRoutes.map(menuRoute => {
        const existingPaths = new Set(flattenMenuRoutes([menuRoute]).map(route => route.path));
        const linkedRoutes = detachedRoutes.filter(route => {
            const activeMenu = route.meta?.activeMenu;
            return (
                typeof activeMenu === "string" &&
                isPathInsideMenu(activeMenu, menuRoute.path || "") &&
                !existingPaths.has(route.path)
            );
        });

        return linkedRoutes.length
            ? { ...menuRoute, children: [...(menuRoute.children || []), ...linkedRoutes] }
            : menuRoute;
    });
}

/**
 * 按旧 Vue 顶部菜单的点击规则查找首个可见叶子页面。
 * 分组自身不作为落地页；隐藏详情页及其子树也不能成为默认入口。
 */
export function getFirstVisibleMenuPath(routes: ReactPermissionRoute[]): string | undefined {
    for (const route of routes) {
        if (route.meta?.hidden === true) {
            continue;
        }

        if (route.children?.length) {
            const childPath = getFirstVisibleMenuPath(route.children);
            if (childPath) {
                return childPath;
            }
            continue;
        }

        if (route.path) {
            return route.path;
        }
    }

    return undefined;
}

export default buildMenuRoutes;
